<template>
  <div class="setting-content--section">
    <p>{{ $gettext('This page shows you an overview of your PSD2 connections to your bank.') }}</p>
    <div v-for="a_consent in enablenowCompanyConsents" :key="a_consent.id" class="consent-box">
      <div class="consent-box--data">
        <h3>{{ $gettext('Consent info') }}</h3>
        <ul>
          <li>
            <span class="label">{{ $gettext('Consent id') }}:</span>
            <span class="value">{{ a_consent.id.substring(0, 8) }}-xxxx-xxxx-xxxx</span>
          </li>
          <li>
            <span class="label">{{ $gettext('Status') }}:</span>
            <span class="value">{{ a_consent.status }}</span>
          </li>
          <li>
            <span class="label">{{ $gettext('Last updated') }}:</span>
            <span class="value">{{ a_consent.dateTransactionsUpdated | formatDate }}</span>
          </li>
          <li>
            <span class="label">{{ $gettext('Expiry date') }}:</span>
            <span class="value">{{ a_consent.dateExpire | formatDate }}</span>
          </li>
          <li>
            <span class="label"></span>
            <span class="value">
              <GekkoButton
                @click="deleteConsent(a_consent.id)"
                :icon="'fa-solid fa-trash-can'"
                :text="$gettext('Delete consent')"
                variant="link"
                size="sm"
                class="text-danger p-0"
              />
            </span>
          </li>
        </ul>
      </div>

      <div class="consent-box--accounts">
        <h3>{{ $gettext('Accounts') }}</h3>
        <b-table
          :items="a_consent.bank_accounts"
          :fields="consentFields"
          striped
          hover
          :class="['gk-table gk-table--responsive gk-table--striped gk-table--headed']"
          show-empty
          table-busy
          :key="counter"
        >
          <template #empty="">
            <span class="empty-table-text" v-translate>No bank accounts.</span>
          </template>
          <template #table-busy="">
            <b-skeleton-table
              :rows="10"
              :columns="4"
              :table-props="{ bordered: false, striped: true }"
              :hide-header="true"
            />
          </template>
          <template #cell(status)="data">
            <div v-if="data.item.status === 'connected'" class="text-success" v-b-tooltip.hover :title="$gettext('The connection is established and working correctly.')">
              <font-awesome-icon icon="fa-regular fa-circle-check" />
            </div>
            <div v-if="data.item.status === 'unconnected'" class="text-blue" @click="connectAccount(data.item)" v-b-tooltip.hover :title="$gettext('Finish connection with the existing bank account from your account.')">
              <font-awesome-icon icon="fa-regular fa-rotate-left" />
            </div>
            <div v-if="data.item.status === 'inexistent'" class="text-blue" @click="connectAccount(data.item)" v-b-tooltip.hover :title="$gettext('Create bank account on your account and connect it to this consent.')">
              <font-awesome-icon icon="fa-solid fa-link" />
            </div>
            <div v-if="data.item.status === 'loading'" class="text-blue">
              <LoadingSpinner button-spinner :size="'sm'" />
            </div>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import settingsPageSetup from '@/mixins/settingsPageSetup'
import BankService from '@/store/services/bank.service.js'
import GekkoButton from '@/components/GekkoButton'

export default {
  name: 'BankSettings',
  components: { GekkoButton, LoadingSpinner },
  mixins: [settingsPageSetup],
  data() {
    return {
      pageSettings: {
        title: this.$gettext('Bank Settings'),
        showCompanyPicker: true,
      },
      counter: 0,
      enablenowCompanyConsents: [],
      gekkoBanks: [],
      consentFields: [
        { key: 'iban', label: this.$gettext('IBAN') },
        { key: 'accountHolder', label: this.$gettext('Holder') },
        { key: 'accountBalance.amount', label: this.$gettext('Balance') },
        { key: 'status', label: this.$gettext('Connection Status'), thClass: 'text-center', tdClass: 'text-center' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId',
      currentCompany: 'main/getCurrentCompany',
      isPremium: 'getIsPremium',
    }),
  },
  created() {
    this.setupData()
  },
  methods: {
    setupData() {
      var params = { company_profile: this.getCompanyId }
      BankService.listCompanyConsents(params).then((response) => {
        this.enablenowCompanyConsents = response.data
        this.prepareRender()
      })
      BankService.list(params).then((response) => {
        this.gekkoBanks = response.data
        this.prepareRender()
      })
    },
    prepareRender() {
      if (this.gekkoBanks.length === 0 || this.enablenowCompanyConsents.length === 0) {
        return
      }

      for (var i = 0; i < this.enablenowCompanyConsents.length; i++) {
        var temp = this.enablenowCompanyConsents[i]
        for (var j = 0; j < temp.bank_accounts.length; j++) {
          var consent_bank_account = temp.bank_accounts[j]
          consent_bank_account.status = 'inexistent'
          consent_bank_account.consent_id = temp.id;
          for (var k = 0; k < this.gekkoBanks.length; k++) {
            if (consent_bank_account.id === this.gekkoBanks[k].external_id) {
              consent_bank_account.status = 'connected'
            }else if(consent_bank_account.iban === this.gekkoBanks[k].iban){
              consent_bank_account.status = 'unconnected'
            }
          }
        }
      }
    },
    deleteConsent(consent_id) {
      var data = { consent_id: consent_id }
      BankService.deleteConsent(data).then((response) => {
        this.setupData()
      })
    },
    connectAccount(bank_account) {
      this.counter += 1
      bank_account.status = 'loading'
      var iban = bank_account.iban
      var consent = bank_account.consent_id
      var bankaccount_id = bank_account.id
      var data = { iban: iban, consent_id: consent, bankaccount_id: bankaccount_id, company_profile: this.getCompanyId}
      BankService.attachConsent(data).then((response) => {
        this.setupData()
      })
    }
  },
}
</script>

<style lang="scss" scoped>
.consent-box {
  display: flex;
  margin-top: 1rem;
  border-radius: $radiusLg;
  padding: 1rem;
  border: 1px solid $colorBorderDash;
  gap: 1rem;
  h3 {
    font-size: 18px;
    margin-bottom: 1rem;
  }
  &--data {
    width: 40%;
  }
  &--accounts {
    width: 60%;
  }
  &--data {
    font-size: 14px;
    color: #333;
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      li {
        display: flex;
        margin-bottom: 0.25rem;

        .label {
          width: 140px;
          flex-shrink: 0;
          color: $colorUnassigned;
        }
        .value {
          flex: 1;
        }
      }
    }
  }
  @media (max-width: '1200px') {
    flex-direction: column;
    &--data,
    &--accounts {
      width: 100%;
    }
  }
}
</style>